import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormikProvider, useFormik } from 'formik';
import { CloseOutlined } from '@ant-design/icons';
import { Button, CancelCreateModal, CreateUser, Loader, Select, Switcher } from '../../../components';
import { BackIcon } from '../../../assets';
import { USER_ROLES } from '../../../constants/constants';
import { createUserSchema } from '../../../utils/validations';
import { Navigation } from '../../../utils/navigation';
import { usePreventLeave } from '../../../hooks/usePreventLeave';
import { fetchHealthOrganization } from '../../../store/health_organizations';
import { createMultiSiteAdmin } from '../../../store/users';
import { facilityOptions, toOrgList } from './MultiSiteAdminForm';
import pageStyles from '../../common/CreateUser/index.module.css';
import formStyles from '../../../components/forms/CreateUser/index.module.css';
import adminStyles from './index.module.css';

const initialValues = {
  email: '',
  firstName: '',
  lastName: '',
  facilityIds: [],
  emailSubscription: false,
};

const CreateMultiSiteAdmin = ({
  currentHealthOrganization,
  organizationId,
  isViewDashboardMode,
  fetchHealthOrganization,
  createMultiSiteAdmin,
}) => {
  const navigate = Navigation();
  const facilities = currentHealthOrganization?.id === organizationId ? currentHealthOrganization.children || [] : [];
  const activeFacilities = facilities.filter((facility) => !facility.disabled);

  useEffect(() => {
    if (organizationId) {
      fetchHealthOrganization(organizationId);
    }
  }, [fetchHealthOrganization, organizationId]);

  const formik = useFormik({
    initialValues,
    validateOnMount: true,
    validationSchema: createUserSchema,
    onSubmit: (values) => {
      unblockHistory?.current && unblockHistory.current();
      return createMultiSiteAdmin({
        Navigation: navigate,
        payload: {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          emailSubscription: values.emailSubscription,
          userRole: USER_ROLES.HO_ADMIN,
          organizationId,
          orgList: toOrgList(values.facilityIds, activeFacilities),
        },
      });
    },
  });

  const [visibleModal, setModalVisible, unblockHistory, location] = usePreventLeave(initialValues, formik.values);

  const handleBack = useCallback(() => {
    const from = navigate?.location?.state?.from;
    if (from) {
      navigate.push(from);
      return;
    }
    navigate.back();
  }, [navigate]);

  const onCancel = useCallback(() => setModalVisible(false), [setModalVisible]);

  const onLeave = useCallback(() => {
    if (unblockHistory?.current) {
      unblockHistory.current();
      navigate.push(location.location);
    }
  }, [navigate, location, unblockHistory]);

  const canSendInvite = formik.isValid && formik.values.facilityIds.length >= 1;

  return (
    <div className={pageStyles.pageContent}>
      <Loader loading={formik.isSubmitting} />
      <div className={pageStyles.header}>
        <div className={pageStyles.backBtn} onClick={handleBack}>
          <BackIcon width="26px" height="26px" fill="#E1F4D3" />
        </div>
        <h1 className={pageStyles.pageTitle}>Add New Admin</h1>
      </div>
      <div>
        <h2>Provide information for the new Admin</h2>
        <FormikProvider value={formik}>
          <form className={pageStyles.form} onSubmit={formik.handleSubmit}>
            <CreateUser
              values={formik.values}
              managedRole={USER_ROLES.HO_ADMIN}
              {...formik}
            />
            <div className={formStyles.listContainer}>
              <label className={formStyles.orgList}>
                <span className={formStyles.required}>Health Organizations</span>
                <Select
                  showSearch
                  name="facilityIds"
                  value={formik.values.facilityIds || []}
                  options={facilityOptions(activeFacilities)}
                  onChange={({ value }) => formik.setFieldValue('facilityIds', value)}
                  placeholder="Health Organizations"
                  mode="multiple"
                  maxTagCount={1}
                  maxTagTextLength={18}
                  myClassName={adminStyles.facilitySelect}
                />
              </label>
              {formik.values.facilityIds.length > 0 && (
                <div className={adminStyles.selectedFacilities}>
                  {formik.values.facilityIds.map((id) => {
                    const facility = activeFacilities.find((item) => item.id === id);
                    if (!facility) return null;
                    return (
                      <span className={adminStyles.tag} key={id}>
                        <span>{facility.name}</span>
                        <CloseOutlined
                          className={adminStyles.closeIcon}
                          onClick={() =>
                            formik.setFieldValue(
                              'facilityIds',
                              formik.values.facilityIds.filter((facilityId) => facilityId !== id),
                            )
                          }
                        />
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {formik.values.facilityIds.length < 1 && (
              <p className={pageStyles.errorMessage}>
                {activeFacilities.length < 1
                  ? 'Add at least one facility before creating a multi-site admin.'
                  : 'Select at least one facility.'}
              </p>
            )}
            {isViewDashboardMode && (
              <>
                <h3 className={formStyles.userEmailContainer}>Communications Preferences</h3>
                <div className={formStyles.toggleContainer}>
                  <div className={formStyles.toggleRow}>
                    <label className={formStyles.toggleLabel}>
                      Unsubscribe me from all mailing lists.
                    </label>
                    <Switcher
                      name="emailSubscription"
                      value={formik.values.emailSubscription}
                      onChange={({ setFieldValue, value }) => {
                        setFieldValue('emailSubscription', value);
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            <div className={pageStyles.btnPanel}>
              <div className={pageStyles.btnControl}>
                <Button myClassName={pageStyles.navigationBtn} onClick={handleBack}>
                  Cancel
                </Button>
              </div>
              <div className={pageStyles.btnControl}>
                <Button disabled={!canSendInvite || formik.isSubmitting} type="submit">
                  Send Invite
                </Button>
              </div>
            </div>
          </form>
        </FormikProvider>
      </div>
      <CancelCreateModal
        title={`Canceling New ${USER_ROLES.HO_ADMIN}`}
        visible={visibleModal}
        onCancel={onCancel}
        onLeave={onLeave}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentHealthOrganization: state.healthOrganizations.currentHealthOrganization,
  organizationId: state.authorization.user?.organizationId,
  isViewDashboardMode: state?.authorization?.viewDashboardMode?.active,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ fetchHealthOrganization, createMultiSiteAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CreateMultiSiteAdmin);
